/**
 * Validation for Robot Programming Challenge
 *
 * This module provides a set of functions to validate the input data for the
 * Robot Programming Challenge.
 *
 * The input data is an Abstract Syntax Tree (AST) that represents the user's
 * robot commands. The AST is an array of command objects, where each command
 * object has the following structure:
 *  {
 *    obj: 'robot',
 *    cmd: 'addMoveCommand' | 'addTurnCommand' | 'addPickupCommand' | 'addDropCommand' | 'addShootCommand',
 *    args: [Arg],
 *    loc: AstLocation
 *  }
 *
 * The 'obj' field is always 'robot' to indicate that the command is for the robot.
 * The 'cmd' field is the command name.
 * The 'args' field is an array of arguments for the command.
 * The 'loc' field is an object with location information for the command.
 *
 * The 'Arg' type is defined as follows:
 *  {
 *    type: 'Number' | 'String',
 *    value: any
 *  }
 *
 * The 'type' field indicates the type of the argument.
 * The 'value' field is the value of the argument.
 *
 * The 'AstLocation' type is defined as follows:
 *  {
 *    start: { offset: number, line: number, column: number },
 *    end: { offset: number, line: number, column: number }
 *  }
 *
 * The 'start' field is the start location of the command.
 * The 'end' field is the end location of the command.
 *
 * The validation functions check the following:
 * - The AST is an array of command objects.
 * - Each command object has the correct structure.
 * - Each command has the correct number and type of arguments.
 * - The arguments have valid values.
 *
 * The validation functions throw an error if the input data is invalid.
 *
 * API:
 * - addMoveCommand(int steps[, string direction]): Add a move command to the list of commands
 * - addTurnCommand(string direction | int angle): Add a turn command to the list of commands
 * - addPickupCommand(): Add a pickup command to the list of commands
 * - addDropCommand(): Add a drop command to the list of commands
 * - addShootCommand(real angle, int power): Add a shoot command to the list of commands
 *
 * @module
 */

import { assert, debug, isInt, isFloat } from './utils'

/** @type {Number} */
const MIN_MOVE_STEPS = 1
/** @type {Number} */
const MAX_MOVE_STEPS = 100

/** @type {Number} */
const MIN_TURN_ANGLE = -180
/** @type {Number} */
const MAX_TURN_ANGLE = 180

/** @type {Number} */
const MIN_SHOOT_ANGLE = 0
/** @type {Number} */
const MAX_SHOOT_ANGLE = 90
/** @type {Number} */
const MIN_SHOOT_POWER = 1
/** @type {Number} */
const MAX_SHOOT_POWER = 100

/**
 * @typedef {Object} AstLocation
 *
 * @property {Object} start
 * @property {number} start.offset
 * @property {number} start.line
 * @property {number} start.column
 *
 * @property {Object} end
 * @property {number} end.offset
 * @property {number} end.line
 * @property {number} end.column
 */

/**
 * @typedef {Object} AstCommand
 *
 * @property {string} obj
 * @property {string} cmd
 * @property {Array.<Arg} args
 * @property {AstLocation} loc
 */

/**
 * Construct an error message with location information.
 *
 * @param {string} msg
 * @param {AstCommand} command
 * @returns {string}
 */
function errMsg (msg, command) {
  return `${msg} at line ${command.loc.start.line}, column ${command.loc.start.column}`
}

/**
 * Validate the AST.
 *
 * @param {Array.<AstCommand>} ast
 * @returns {boolean}
 *
 * @throws {Error} If the AST is invalid
 */
export default function validate (ast) {
  debug('>> validate')
  validateAst(ast)
  for (const command of ast) {
    validateCommand(command)
  }
  return true
}

/**
 * Validate the AST.
 *
 * @param {Array.<AstCommand>} ast
 * @returns {void}
 *
 * @throws {Error} If the AST is invalid
 */
function validateAst (ast) {
  debug('>> validateAst')
  // Validate the AST
  assert(Array.isArray(ast), 'AST must be an array')
  for (const command of ast) {
    assert(typeof command === 'object', 'Command must be an object')
    assert(command.obj === 'robot', 'Command object must be "robot"')
    assert(typeof command.cmd === 'string', 'Command must be a string')
    assert(Array.isArray(command.args), 'Command arguments must be an array')
  }
}

/**
 * Validate an AST command.
 *
 * @param {AstCommand} command
 * @returns {void}
 *
 * @throws {Error} If the command is invalid
 */
function validateCommand (command) {
  debug('>> validateCommand', command.cmd)
  switch (command.cmd) {
    case 'addMoveCommand':
      validateMoveCommand(command)
      break
    case 'addTurnCommand':
      validateTurnCommand(command)
      break
    case 'addPickupCommand':
      validatePickupCommand(command)
      break
    case 'addDropCommand':
      validateDropCommand(command)
      break
    case 'addShootCommand':
      validateShootCommand(command)
      break
    default:
      assert(false, `Unknown command: ${command.cmd}`)
  }
}

/**
 * Validate a move command.
 *
 * @param {AstCommand} command
 * @returns {void}
 *
 * @throws {Error} If the command is invalid
 */
function validateMoveCommand (command) {
  debug('>> validateMoveCommand', command)

  // Validate the move command steps argument
  function validateMoveCommandStepsArg (arg, command) {
    debug('>> validateMoveCommandStepsArg', arg)
    assert(
      arg.type === 'Number' && isInt(arg.value),
      errMsg('Move command steps argument must be an int', command)
    )
    assert(
      arg.value >= MIN_MOVE_STEPS,
      errMsg(
        `Move command steps argument must be greater than or equal to ${MIN_MOVE_STEPS}`,
        command
      )
    )
    assert(
      arg.value <= MAX_MOVE_STEPS,
      errMsg(
        `Move command steps argument must be less than or equal to ${MAX_MOVE_STEPS}`,
        command
      )
    )
  }

  // Validate the move command direction argument
  function validateMoveCommandDirectionArg (arg, command) {
    debug('>> validateMoveCommandDirectionArg', arg)
    assert(
      arg.type === 'String',
      errMsg('Move command direction argument must be a string', command)
    )
    assert(
      arg.value === 'forward' || arg.value === 'backward',
      errMsg(
        'Move command direction argument must be either "forward" or "backward"',
        command
      )
    )
  }

  if (command.args.length === 1) {
    validateMoveCommandStepsArg(command.args[0], command)
  } else if (command.args.length === 2) {
    validateMoveCommandStepsArg(command.args[0], command)
    validateMoveCommandDirectionArg(command.args[1], command)
  } else {
    assert(false, errMsg('Move command must have 1 or 2 arguments', command))
  }
}

/**
 * Validate a turn command.
 *
 * @param {AstCommand} command
 * @returns {void}
 *
 * @throws {Error} If the command is invalid
 */
function validateTurnCommand (command) {
  debug('>> validateTurnCommand', command)
  assert(
    command.args.length === 1,
    errMsg('Turn command must have 1 argument', command)
  )
  if (command.args[0].type === 'Number') {
    assert(
      isInt(command.args[0].value) &&
        command.args[0].value <= MAX_TURN_ANGLE &&
        command.args[0].value >= MIN_TURN_ANGLE,
      errMsg(
        `Turn command angle argument must be an integer between ${MIN_TURN_ANGLE} and ${MAX_TURN_ANGLE}, inclusive`,
        command
      )
    )
  } else if (command.args[0].type === 'String') {
    assert(
      command.args[0].value === 'left' || command.args[0].value === 'right',
      errMsg(
        'Turn command direction argument must be "left" or "right"',
        command
      )
    )
  } else {
    assert(
      false,
      errMsg(
        'Turn command argument must be an angle:int or a direction:string',
        command
      )
    )
  }
}

/**
 * Validate a pickup command.
 *
 * @param {AstCommand} command
 * @returns {void}
 *
 * @throws {Error} If the command is invalid
 */
function validatePickupCommand (command) {
  debug('>> validatePickupCommand', command)
  assert(
    command.args.length === 0,
    errMsg('Pickup command must have 0 arguments', command)
  )
}

/**
 * Validate a drop command.
 *
 * @param {AstCommand} command
 * @returns {void}
 *
 * @throws {Error} If the command is invalid
 */
function validateDropCommand (command) {
  debug('>> validateDropCommand', command)
  assert(
    command.args.length === 0,
    errMsg('Drop command must have 0 arguments', command)
  )
}

/**
 * Validate a shoot command.
 *
 * @param {AstCommand} command
 * @returns {void}
 *
 * @throws {Error} If the command is invalid
 */
function validateShootCommand (command) {
  debug('>> validateShootCommand', command)

  // Validate the shoot command angle argument
  function validateShootCommandAngleArg (arg, command) {
    debug('>> validateShootCommandAngleArg', arg)
    assert(
      arg.type === 'Number' && isFloat(arg.value),
      errMsg('Shoot command angle argument must be a float', command)
    )
    assert(
      arg.value >= MIN_SHOOT_ANGLE,
      errMsg(
        `Shoot command angle argument must be greater than or equal to ${MIN_SHOOT_ANGLE}`,
        command
      )
    )
    assert(
      arg.value <= MAX_SHOOT_ANGLE,
      errMsg(
        `Shoot command angle argument must be less than or equal to ${MAX_SHOOT_ANGLE}`,
        command
      )
    )
  }

  // Validate the shoot command power argument
  function validateShootCommandPowerArg (arg, command) {
    debug('>> validateShootCommandPowerArg', arg)
    assert(
      arg.type === 'Number' && isInt(arg.value),
      errMsg('Shoot command power argument must be an int', command)
    )
    assert(
      arg.value >= MIN_SHOOT_POWER,
      errMsg(
        `Shoot command power argument must be greater than or equal to ${MIN_SHOOT_POWER}`,
        command
      )
    )
    assert(
      arg.value <= MAX_SHOOT_POWER,
      errMsg(
        `Shoot command power argument must be less than or equal to ${MAX_SHOOT_POWER}`,
        command
      )
    )
  }

  if (command.args.length === 1) {
    validateShootCommandAngleArg(command.args[0], command)
  } else if (command.args.length === 2) {
    validateShootCommandAngleArg(command.args[0], command)
    validateShootCommandPowerArg(command.args[1], command)
  } else {
    assert(false, errMsg('Shoot command must have 1 or 2 arguments', command))
  }
}
